import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Download, Search, Users, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

interface WaitlistSignup {
  id: string;
  email: string;
  ip_address: string | null;
  user_agent: string | null;
  referrer: string | null;
  created_at: string;
}

export default function AdminWaitlistPage() {
  const [searchQuery, setSearchQuery] = useState('');

  const { data: signups, isLoading } = useQuery({
    queryKey: ['waitlist-signups'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('waitlist_signups')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as WaitlistSignup[];
    },
  });

  const filteredSignups = signups?.filter((signup) =>
    signup.email.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const handleExportCSV = () => {
    if (!signups || signups.length === 0) return;

    const headers = ['Email', 'Signup Date', 'Referrer', 'User Agent'];
    const rows = signups.map((s) => [
      s.email,
      format(new Date(s.created_at), 'yyyy-MM-dd HH:mm:ss'),
      s.referrer || '',
      s.user_agent || '',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `waitlist-signups-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Waitlist Signups</h1>
        <p className="text-muted-foreground">
          Manage pre-launch email signups
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Signups</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{signups?.length || 0}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle>Email List</CardTitle>
              <CardDescription>
                All users who signed up for the waitlist
              </CardDescription>
            </div>
            <Button
              onClick={handleExportCSV}
              disabled={!signups || signups.length === 0}
              variant="outline"
            >
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 max-w-sm"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredSignups.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchQuery ? 'No signups match your search' : 'No waitlist signups yet'}
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Signup Date</TableHead>
                    <TableHead>Referrer</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSignups.map((signup) => (
                    <TableRow key={signup.id}>
                      <TableCell className="font-medium">{signup.email}</TableCell>
                      <TableCell>
                        {format(new Date(signup.created_at), 'MMM d, yyyy h:mm a')}
                      </TableCell>
                      <TableCell>
                        {signup.referrer ? (
                          <Badge variant="outline" className="truncate max-w-[200px]">
                            {signup.referrer}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">Direct</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
